import { makeAutoObservable } from 'mobx'
import { RootStore } from '@/app/store'
import { ParsedDeliverable } from '@/utils/deliverableParser'
import { extractAllArtifactsFromThread } from '../chat/utils'
import { isSameArtifact } from './utils'

/**
 * Store para gestionar el estado del artifact viewer
 */
export class ArtifactStore {
  rootStore: RootStore

  // Estado observable
  artifactViewerOpen: boolean = false
  selectedArtifact: ParsedDeliverable | null = null
  artifactViewMode: 'side-by-side' | 'single-column' = 'side-by-side'
  isInlineExpanded: boolean = false // Para mobile: el artifact se expande inline

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  // Computed: obtener todos los artifacts del thread actual
  public get allArtifacts(): ParsedDeliverable[] {
    if (!this.rootStore.chat?.thread?.messages) {
      return []
    }
    return extractAllArtifactsFromThread(this.rootStore.chat.thread.messages)
  }

  /**
   * Abre el artifact viewer con el artifact especificado
   * @param artifact - El artifact a mostrar
   * @param isMobile - Si es mobile, se expande inline en lugar del viewer
   */
  public openArtifactViewer(artifact: ParsedDeliverable, isMobile = false) {
    this.artifactViewerOpen = true
    this.selectedArtifact = artifact
    this.isInlineExpanded = isMobile
  }

  /**
   * Cierra el artifact viewer
   */
  public closeArtifactViewer() {
    this.artifactViewerOpen = false
    this.selectedArtifact = null
    this.artifactViewMode = 'side-by-side'
    this.isInlineExpanded = false
  }

  /**
   * Selecciona un artifact diferente sin cerrar el viewer
   * @param artifact - El artifact a seleccionar
   */
  public selectArtifact(artifact: ParsedDeliverable) {
    this.selectedArtifact = artifact
  }

  /**
   * Verifica si un artifact está actualmente seleccionado
   * @param artifact - El artifact a verificar
   * @returns true si el artifact está seleccionado
   */
  public isArtifactSelected(artifact: ParsedDeliverable): boolean {
    return isSameArtifact(this.selectedArtifact, artifact)
  }

  /**
   * Alterna entre modos de vista side-by-side y single-column
   */
  public toggleArtifactViewMode() {
    this.artifactViewMode =
      this.artifactViewMode === 'side-by-side'
        ? 'single-column'
        : 'side-by-side'
  }

  /**
   * Resetea el estado del artifact viewer
   */
  public resetState() {
    this.artifactViewerOpen = false
    this.selectedArtifact = null
    this.artifactViewMode = 'side-by-side'
    this.isInlineExpanded = false
  }
}
